/* Shared, dependency-free transport marketing client. The API owns every price. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function marketingToken(value) {
  return typeof value === 'string' && UUID.test(value) ? value : '';
}

export function marketingQuery(query = {}) {
  const get = key => typeof query.get === 'function' ? query.get(key) : query[key];
  return { promo: marketingToken(get('promo')), mc: marketingToken(get('mc')), md: marketingToken(get('md')), invalid: Boolean(get('promo') && !marketingToken(get('promo'))) };
}

export function promotionSelection(booking = {}) {
  const keys = ['company_id', 'destinationID', 'origin', 'destination', 'originZone', 'destinationZone', 'originPlaceId', 'destinationPlaceId', 'trip_distance_m', 'trip_duration_s', 'serviceType', 'transportType', 'aptRes_type', 'isOriginAirport', 'isDestinationAirport', 'isArrival', 'isDeparture', 'ArrivalDate', 'DepartureDate', 'DepartureTime', 'pickupDate', 'pickupTime', 'returnDate', 'returnTime', 'arrivalFlightDate', 'arrivalFlightTime', 'departureFlightDate', 'departureFlightTime', 'adults', 'kids', 'infants', 'hours', 'currency', 'allyCode', 'comm_partner_email', 'resolved_referral_type', 'resolved_referral_value'];
  const result = {};
  keys.forEach(key => { result[key] = booking[key] ?? null; });
  result.vehicle = { id: booking.vehicles?.vehiclePerCompany_id, quantity: booking.vehicles?.Quantity };
  result.addons = (booking.addons || []).map(addon => ({ id: addon.id ?? addon.TransportationAddon_id, quantity: addon.quantity, leg: addon.leg ?? null })).sort((a, b) => String(a.id).localeCompare(String(b.id)));
  return JSON.stringify(result);
}

export function promotionBookingPayload(booking, companyId) {
  const payload = JSON.parse(JSON.stringify(booking || {}));
  payload.company_id = Number(companyId || payload.company_id);
  // Do not reinterpret airport direction. The existing booking flow supplies this value.
  payload.aptRes_type = Number(payload.isOriginAirport) === 1 || Number(payload.isDestinationAirport) === 1 ? 1 : 2;
  delete payload.promotion_quote;
  return payload;
}

export async function marketingRequest(baseUrl, path, options = {}) {
  const response = await fetch(`${String(baseUrl).replace(/\/?$/, '/')}${path}`, {
    ...options, headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const json = await response.json();
  if (!response.ok || json.error || !json.data) {
    const error = new Error(json.message || 'The promotion could not be validated.');
    error.status = response.status;
    throw error;
  }
  return json.data;
}

export async function resolveMarketingContext(baseUrl, companyId, input) {
  const query = marketingQuery(input);
  if (query.invalid) throw new Error('Invalid promotion link.');
  const context = { companyId: Number(companyId), query, promotion: null, campaign: null };
  const params = new URLSearchParams({ company_id: String(companyId) });
  if (query.md) params.set('delivery_token', query.md);
  if (query.promo) {
    context.promotion = await marketingRequest(baseUrl, `public/transport-promotions/${encodeURIComponent(query.promo)}?${params}`);
    if (Number(context.promotion.company_id) !== Number(companyId)) throw new Error('Invalid promotion company.');
  }
  if (query.mc) {
    context.campaign = await marketingRequest(baseUrl, `public/transport-marketing/campaigns/${encodeURIComponent(query.mc)}?${params}`);
    if (Number(context.campaign.company_id) !== Number(companyId)) throw new Error('Invalid campaign company.');
  }
  return context;
}

export function marketingFields(context, { includePromotion = true, consent } = {}) {
  if (!context) return typeof consent === 'boolean' ? { marketing_consent: consent } : {};
  const fields = {};
  if (context.query.mc && context.campaign) fields.marketing_campaign_token = context.query.mc;
  else if (context.promotion?.campaign_id) fields.marketing_campaign_token = marketingToken(context.promotion.campaign_token) || context.query.promo;
  if (context.query.md && (context.promotion || context.campaign)) fields.marketing_delivery_token = context.query.md;
  if (includePromotion && context.promotion) fields.promotion_token = context.query.promo;
  if (typeof consent === 'boolean') fields.marketing_consent = consent;
  return fields;
}

export async function requestPromotionQuote(baseUrl, context, booking) {
  if (!context?.promotion || !context.query.promo) throw new Error('No valid promotion.');
  const data = await marketingRequest(baseUrl, `public/transport-promotions/${encodeURIComponent(context.query.promo)}/quote`, {
    method: 'POST',
    body: JSON.stringify({ company_id: context.companyId, delivery_token: context.query.md || null, bookingObject: promotionBookingPayload(booking, context.companyId) }),
  });
  for (const field of ['subtotal_before_discount', 'eligible_subtotal', 'discount_amount', 'subtotal', 'tax_amount', 'total', 'vehicle_total']) {
    if (!Number.isFinite(Number(data[field])) || Number(data[field]) < 0) throw new Error('Invalid promotion quote.');
  }
  if (!marketingToken(data.quote_token) || !data.expires_at || !Number.isFinite(Date.parse(data.expires_at)) || Date.parse(data.expires_at) <= Date.now()) throw new Error('Expired promotion quote.');
  return data;
}

export function applyPromotionQuote(booking, quote, company = {}) {
  const next = { ...booking, vehicles: { ...booking.vehicles, totalAmount: Number(quote.vehicle_total) }, subtotal: Number(quote.subtotal), subtotal_before_discount: Number(quote.subtotal_before_discount), discount_amount: Number(quote.discount_amount), tax_amount: Number(quote.tax_amount), tax: Number(quote.tax_percent || 0), total: Number(quote.total), currency: quote.currency || booking.currency, promotion_quote_token: quote.quote_token, promotion_quote: quote, promoCode: quote.promo_code || '', couponApplied: false, couponAlreadyApplied: 0, couponAmount: 0, couponType: 0, coupon_apply_on: 0 };
  const prepay = [true, 1, '1', 'true'].includes(next.isPrepay);
  const percent = Number(company.booking_options?.[0]?.bookingOptions_partial_payment || 0) / 100;
  next.prepay_amount = prepay ? Number((next.total * percent).toFixed(2)) : 0;
  next.prepay_due = Number((next.total - next.prepay_amount).toFixed(2));
  return next;
}
